import { createDeck } from '@/engine/deck';
import type { HandEvaluator, Rng } from '@/engine/ports';
import type { Card, PlayerId } from '@/engine/types';
import type { EquityResult } from '../types';

const HERO: PlayerId = '__hero';

export type EquityOptions = {
  /** Number of Monte Carlo simulations. Default 10_000 (plan G5). */
  trials?: number;
  rng: Rng;
  evaluator: HandEvaluator;
};

/**
 * Estimate hero equity via Monte Carlo simulation against uniformly random
 * villain hole cards drawn from the remaining deck. Complete the board to
 * 5 cards, evaluate, tally win/tie/lose + fractional equity (accounts for
 * odd-way chops — hero's share = 1 / winners.length on a tie).
 *
 * Deterministic given a seeded Rng. Villain range biasing is layered on top
 * by the caller (see range.ts / decide.ts).
 */
export function computeEquity(
  hole: readonly [Card, Card],
  board: ReadonlyArray<Card>,
  numVillains: number,
  options: EquityOptions,
): EquityResult {
  if (numVillains < 1) {
    throw new RangeError(`computeEquity: numVillains must be >= 1, got ${numVillains}`);
  }
  const trials = options.trials ?? 10_000;
  if (trials < 1) {
    throw new RangeError(`computeEquity: trials must be >= 1, got ${trials}`);
  }

  const used = new Set<Card>([...hole, ...board]);
  const deck = createDeck().filter((c) => !used.has(c));
  const boardNeeded = 5 - board.length;
  if (boardNeeded < 0) {
    throw new RangeError(`computeEquity: board has more than 5 cards (${board.length})`);
  }
  const cardsPerSim = numVillains * 2 + boardNeeded;
  if (cardsPerSim > deck.length) {
    throw new RangeError(`computeEquity: need ${cardsPerSim} cards but deck has ${deck.length}`);
  }

  const villainIds: PlayerId[] = Array.from({ length: numVillains }, (_, i) => `__v${i}`);

  // Reusable buffer for the partial Fisher-Yates below. We only need the
  // first `cardsPerSim` positions per trial, so swapping the whole ~47-card
  // deck each iteration (what `rng.shuffle` does) is wasteful. Shuffle
  // exactly k cards into the prefix of a mutable copy and read them off.
  const buf = deck.slice();
  const deckLen = buf.length;

  let wins = 0;
  let ties = 0;
  let losses = 0;
  let equityAccum = 0;

  for (let t = 0; t < trials; t++) {
    for (let i = 0; i < cardsPerSim; i++) {
      const j = i + options.rng.int(deckLen - i);
      const tmp = buf[i] as Card;
      buf[i] = buf[j] as Card;
      buf[j] = tmp;
    }
    let cursor = 0;
    const contenders: Array<{ playerId: PlayerId; holeCards: readonly [Card, Card] }> = [
      { playerId: HERO, holeCards: hole },
    ];
    for (let v = 0; v < numVillains; v++) {
      const vHole: readonly [Card, Card] = [buf[cursor++] as Card, buf[cursor++] as Card];
      contenders.push({ playerId: villainIds[v] as PlayerId, holeCards: vHole });
    }
    const fullBoard: Card[] = [...board];
    for (let i = 0; i < boardNeeded; i++) fullBoard.push(buf[cursor++] as Card);

    const winners = options.evaluator.winners(contenders, fullBoard);
    if (winners.includes(HERO)) {
      if (winners.length === 1) {
        wins += 1;
        equityAccum += 1;
      } else {
        ties += 1;
        equityAccum += 1 / winners.length;
      }
    } else {
      losses += 1;
    }
  }

  return {
    trials,
    win: wins / trials,
    tie: ties / trials,
    lose: losses / trials,
    equity: equityAccum / trials,
  };
}
